import { api, postJson } from '@/lib/api';
import type { BootstrapUser } from '@/lib/types';

/**
 * The console's own sign-in and sign-out.
 *
 * Until now the console borrowed the Django admin's login page and linked
 * "Sign out" to its logout URL, which stopped working once that admin became
 * break-glass only (its logout refuses GET). These calls talk to the console
 * API instead; the server still applies the one credential rule, so who may
 * sign in has not changed.
 */

export interface AuthSession {
  authenticated: boolean;
  has_console_access: boolean;
  user: BootstrapUser | null;
  business_name: string;
  login_url: string;
  home_url: string;
  legacy_login_url: string;
}

export interface SignInResult {
  ok: boolean;
  next: string;
  user: BootstrapUser;
}

export const CONSOLE_HOME = '/admin/';

/**
 * Where to land after sign-in. Mirrors the server's rule so the page never
 * even proposes an off-site or break-glass destination: only a console path,
 * and never the login or logout page itself.
 */
export function safeNextPath(raw: string | null | undefined): string {
  const value = (raw ?? '').trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return CONSOLE_HOME;
  if (value !== '/admin' && !value.startsWith('/admin/')) return CONSOLE_HOME;
  if (value.startsWith('/admin/login') || value.startsWith('/admin/logout')) return CONSOLE_HOME;
  return value;
}

export function fetchSession(signal?: AbortSignal): Promise<AuthSession> {
  return api.get<AuthSession>('/auth/session/', undefined, signal);
}

export function signIn(input: { username: string; password: string; next: string }): Promise<SignInResult> {
  return api.post<SignInResult>('/auth/login/', input);
}

export function signOut(): Promise<{ ok: boolean; login_url: string }> {
  return api.post<{ ok: boolean; login_url: string }>('/auth/logout/');
}

/** The public reset flow the mobile and desktop apps already use. */
export function requestPasswordReset(email: string): Promise<{ message?: string }> {
  return postJson<{ message?: string; ok?: boolean }>('/api/auth/initiate-password-reset/', { email });
}

/**
 * A full navigation, not a client-side route change: after sign-in the
 * session cookie is new and the console must boot from scratch; after sign-out
 * nothing cached may survive. Indirected so tests can observe it.
 */
export function navigateTo(url: string): void {
  window.location.assign(url);
}
